/**
 * LiveSessionsPage — "users currently in session, ordered by highest usage"
 * (admin-wave). Built for the tester who hit the per-IP chat rate limit while
 * their own per-user count was well under it — this screen is the "who is
 * actually burning the shared chat budget right now" view. Reuses the shared
 * `AdminTable` scaffold (loading Skeleton / error / empty states) exactly
 * like `ai-usage` (`AiUsagePage.tsx`) and `app-config` (`AppConfigPage.tsx`).
 *
 * No design-prototype screen covers this surface (a "live sessions" table is
 * not among the 13 reference screens in `docs/GardEn/DESIGN-SYSTEM.md`) — per
 * GUARDRAILS rule 10 this is drafted CONSISTENT with the existing system
 * (AdminTable anatomy, GPill tones, `--g-*` tokens, the 30s poll convention
 * `ooc`/`feedback`/`product-review` already use for "what's happening right
 * now" queues) rather than inventing new visual language. Flagged in the PR.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminTable, type AdminTableColumn } from "@/features/admin/components/AdminTable";
import { limitStatusTone } from "@/features/admin/components/tone";
import { useLiveSessions } from "@/features/admin/live-sessions/hooks";
import type {
  LiveSessionsOrderBy,
  LiveSessionUser,
} from "@/features/admin/live-sessions/api";

const ORDERABLE_KEYS: readonly LiveSessionsOrderBy[] = ["chat_hour", "cost"];

function isOrderableKey(key: string): key is LiveSessionsOrderBy {
  return (ORDERABLE_KEYS as readonly string[]).includes(key);
}

/** "3m 12s" style — the countdown to the hour window rolling off. `null`/`0` -> "—". */
function formatResetIn(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function LiveSessionsPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const [orderBy, setOrderBy] = useState<LiveSessionsOrderBy>("chat_hour");
  const { data, isLoading, isError } = useLiveSessions(orderBy);
  const rows: LiveSessionUser[] = data?.users ?? [];

  const columns: AdminTableColumn[] = [
    { key: "name", label: t("liveSessions.columns.name") },
    { key: "sessions", label: t("liveSessions.columns.sessions"), align: "right" },
    {
      key: "chat_hour",
      label: t("liveSessions.columns.chatHour"),
      align: "right",
      sortable: true,
    },
    { key: "chat_min", label: t("liveSessions.columns.chatMin"), align: "right" },
    { key: "reset_in", label: t("liveSessions.columns.resetIn"), align: "right" },
    { key: "limit_status", label: t("liveSessions.columns.limitStatus") },
    {
      key: "cost",
      label: t("liveSessions.columns.cost"),
      align: "right",
      sortable: true,
    },
    { key: "tokens", label: t("liveSessions.columns.tokens"), align: "right" },
  ];

  const limitPill = (status: LiveSessionUser["limit_status"]): ReactElement => (
    <GPill tone={limitStatusTone(status)} size="sm">
      {t(`liveSessions.limitStatusValue.${status}`)}
    </GPill>
  );

  const renderCell = (u: LiveSessionUser, key: string): ReactElement | string => {
    switch (key) {
      case "name":
        return (
          <span>
            <span className="font-medium">{u.display_name}</span>
            <span className="block text-xs text-[var(--g-secondary)]">{u.email}</span>
          </span>
        );
      case "sessions":
        return String(u.session_count);
      case "chat_hour":
        return String(u.chat_requests_hour);
      case "chat_min":
        return String(u.chat_requests_min);
      case "reset_in":
        return formatResetIn(u.hour_window_reset_seconds);
      case "limit_status":
        return limitPill(u.limit_status);
      case "cost":
        return `${u.cost_kgs_today.toFixed(2)} ${t("liveSessions.currency")}`;
      case "tokens":
        return t("liveSessions.tokensValue", {
          input: u.input_tokens_today,
          output: u.output_tokens_today,
        });
      default:
        return "";
    }
  };

  const renderMobileCard = (u: LiveSessionUser): ReactElement => (
    <div className="flex w-full flex-col gap-1">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium">{u.display_name}</div>
          <div className="text-sm text-[var(--g-secondary)]">{u.email}</div>
        </div>
        {limitPill(u.limit_status)}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-[var(--g-secondary)]">
        <span>{t("liveSessions.columns.sessions")}: {u.session_count}</span>
        <span>
          {t("liveSessions.columns.chatHour")}: {u.chat_requests_hour}
        </span>
        <span>
          {t("liveSessions.columns.chatMin")}: {u.chat_requests_min}
        </span>
        <span>
          {t("liveSessions.columns.resetIn")}: {formatResetIn(u.hour_window_reset_seconds)}
        </span>
        <span>
          {t("liveSessions.columns.cost")}: {u.cost_kgs_today.toFixed(2)}{" "}
          {t("liveSessions.currency")}
        </span>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-live-sessions-page" className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-bold">{t("liveSessions.title")}</h1>
        <p className="mt-1 text-sm text-[var(--g-secondary)]">{t("liveSessions.subtitle")}</p>
      </div>

      {data?.truncated ? (
        <div
          role="status"
          data-testid="admin-live-sessions-truncated"
          className="rounded-[var(--g-radius)] px-4 py-3 text-sm font-medium text-white"
          style={{ backgroundColor: "var(--g-warn)" }}
        >
          {t("liveSessions.truncated", { count: data.scanned_keys })}
        </div>
      ) : null}

      <AdminTable<LiveSessionUser>
        columns={columns}
        rows={rows}
        rowKey={(u) => u.user_id}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        rowAriaLabel={(u) => u.display_name}
        testid="admin-live-sessions"
        isLoading={isLoading}
        isError={isError}
        emptyLabel={t("liveSessions.empty")}
        errorLabel={t("error.loadFailed")}
        sort={{
          key: orderBy,
          direction: "desc",
          onSort: (key) => {
            if (isOrderableKey(key)) setOrderBy(key);
          },
          ariaLabel: (column, direction) =>
            direction === "desc"
              ? t("common.sort.descending", { column: column.label })
              : t("common.sort.unsorted", { column: column.label }),
        }}
      />
    </div>
  );
}

export default LiveSessionsPage;
